import { DatePicker, Select, Table, Spin, Row, Col } from 'antd';
import dayjs from 'dayjs';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from 'recharts';
import { formatCurrency } from '@/lib/utils';
import {
  FLUXO_CAIXA_CATEGORIA_LABELS,
  FLUXO_CAIXA_TIPO_LABELS,
  FluxoCaixaCategoriaLinha,
  FluxoCaixaPeriodo,
  FluxoCaixaTipo,
} from '../types/fluxoCaixa';

const { RangePicker } = DatePicker;

const CORES_CATEGORIA = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981',
  '#06b6d4', '#84cc16', '#f97316', '#ef4444', '#3b82f6',
];

interface FluxoCaixaCategoriaRelatorioProps {
  linhas: FluxoCaixaCategoriaLinha[];
  periodo: FluxoCaixaPeriodo;
  tipo: FluxoCaixaTipo | null;
  loading?: boolean;
  onPeriodoChange: (periodo: FluxoCaixaPeriodo) => void;
  onTipoChange: (tipo: FluxoCaixaTipo | null) => void;
}

export function FluxoCaixaCategoriaRelatorio({
  linhas,
  periodo,
  tipo,
  loading = false,
  onPeriodoChange,
  onTipoChange,
}: FluxoCaixaCategoriaRelatorioProps) {
  const dadosPizza = linhas
    .map((linha, index) => ({
      name: FLUXO_CAIXA_CATEGORIA_LABELS[linha.categoria],
      value: tipo === FluxoCaixaTipo.ENTRADA
        ? linha.entrada
        : tipo === FluxoCaixaTipo.SAIDA
          ? linha.saida
          : linha.entrada + linha.saida,
      color: CORES_CATEGORIA[index % CORES_CATEGORIA.length],
    }))
    .filter(item => item.value > 0);

  const columns = [
    {
      title: 'Categoria',
      dataIndex: 'categoria',
      key: 'categoria',
      render: (cat: FluxoCaixaCategoriaLinha['categoria']) => FLUXO_CAIXA_CATEGORIA_LABELS[cat],
    },
    {
      title: 'Entrada',
      dataIndex: 'entrada',
      key: 'entrada',
      align: 'right' as const,
      render: (v: number) => <span className="text-green-600">{formatCurrency(v)}</span>,
    },
    {
      title: 'Saída',
      dataIndex: 'saida',
      key: 'saida',
      align: 'right' as const,
      render: (v: number) => <span className="text-red-600">{formatCurrency(v)}</span>,
    },
    {
      title: 'Líquido',
      dataIndex: 'liquido',
      key: 'liquido',
      align: 'right' as const,
      render: (v: number) => (
        <span className={v >= 0 ? 'text-green-600' : 'text-red-600'}>{formatCurrency(v)}</span>
      ),
    },
    {
      title: '% do Total',
      dataIndex: 'percentual',
      key: 'percentual',
      align: 'right' as const,
      render: (v: number) => `${v.toFixed(1)}%`,
      width: 100,
    },
  ];

  return (
    <div>
      <Row gutter={[12, 12]} className="mb-4">
        <Col xs={24} md={10}>
          <label className="text-xs text-gray-500 dark:text-zinc-400 font-medium block mb-1">
            Período
          </label>
          <RangePicker
            className="w-full"
            format="DD/MM/YYYY"
            value={[dayjs(periodo.dataInicio), dayjs(periodo.dataFim)]}
            onChange={dates => {
              if (!dates?.[0] || !dates?.[1]) return;
              onPeriodoChange({
                dataInicio: dates[0].format('YYYY-MM-DD'),
                dataFim: dates[1].format('YYYY-MM-DD'),
              });
            }}
            disabledDate={current => current && current > dayjs().endOf('day')}
          />
        </Col>
        <Col xs={24} md={6}>
          <label className="text-xs text-gray-500 dark:text-zinc-400 font-medium block mb-1">
            Tipo
          </label>
          <Select
            className="w-full"
            allowClear
            placeholder="Ambos"
            value={tipo ?? undefined}
            onChange={(value: FluxoCaixaTipo | undefined) => onTipoChange(value ?? null)}
          >
            {Object.values(FluxoCaixaTipo).map(t => (
              <Select.Option key={t} value={t}>
                {FLUXO_CAIXA_TIPO_LABELS[t]}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>

      {loading ? (
        <div className="flex justify-center py-12">
          <Spin />
        </div>
      ) : linhas.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400 text-center py-12">
          Nenhum lançamento neste período
        </p>
      ) : (
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={14}>
            <Table
              columns={columns}
              dataSource={linhas}
              rowKey="categoria"
              size="small"
              pagination={false}
              scroll={{ x: 600 }}
            />
          </Col>
          <Col xs={24} lg={10}>
            {dadosPizza.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={dadosPizza}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={95}
                    label={({ name, percent }) =>
                      (percent ?? 0) > 0.05 ? `${name} ${((percent ?? 0) * 100).toFixed(0)}%` : ''
                    }
                  >
                    {dadosPizza.map(item => (
                      <Cell key={item.name} fill={item.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-500 text-center py-16">Sem dados para o gráfico</p>
            )}
          </Col>
        </Row>
      )}
    </div>
  );
}
